import { useState } from "react";
import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { HorizontalLine } from "@design-system/HorizontalLine/HorizontalLine";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { DownloadIcon } from "@icons";
import {
    ChartOfAccountsTemplateImportSource,
    EXPORT_UPLOAD_SOURCES,
    getExportUploadSourceTooltip,
    KICK_TEMPLATE_UPLOAD_SOURCE,
    QUICKBOOKS_CONNECTION_OPTION,
} from "./uploadSources";
import styles from "./ChartOfAccountsTemplateUpload.module.scss";

interface Props {
    title: string;
    show: boolean;
    onClose: () => void;
    isPending: boolean;
    onNext: (source: ChartOfAccountsTemplateImportSource) => void;
}

export const ChartOfAccountsTemplateUploadSourcePicker = ({
    title,
    show,
    onClose,
    isPending,
    onNext,
}: Props) => {
    const [selectedSource, setSelectedSource] =
        useState<ChartOfAccountsTemplateImportSource | null>(null);

    return (
        <Modal show={show} onClose={onClose} size="lg">
            <StandardModalHeader>{title}</StandardModalHeader>
            <StandardModalBody>
                <p className={styles.description}>
                    Choose where your current Chart of Accounts lives.
                </p>
                <RadioGroup<ChartOfAccountsTemplateImportSource | null>
                    aria-label="Chart of Accounts source"
                    value={selectedSource}
                    onValueChange={setSelectedSource}
                >
                    <div className={styles.sourceList}>
                        <Tooltip content={QUICKBOOKS_CONNECTION_OPTION.tooltip}>
                            <Radio.Root
                                value={QUICKBOOKS_CONNECTION_OPTION.choice}
                                className={styles.sourceOption}
                            >
                                <InstitutionIcon
                                    institution={
                                        QUICKBOOKS_CONNECTION_OPTION.institution
                                    }
                                    size="xs"
                                />
                                {QUICKBOOKS_CONNECTION_OPTION.label}
                            </Radio.Root>
                        </Tooltip>
                    </div>
                    <HorizontalLine className={styles.sourceSeparator}>
                        or upload a file
                    </HorizontalLine>
                    <div className={styles.sourceList}>
                        {EXPORT_UPLOAD_SOURCES.map((source) => (
                            <Tooltip
                                key={source.source}
                                content={getExportUploadSourceTooltip(source)}
                            >
                                <Radio.Root
                                    value={source.source}
                                    className={styles.sourceOption}
                                >
                                    <InstitutionIcon
                                        institution={source.institution}
                                        size="xs"
                                    />
                                    {source.pickerLabel}
                                </Radio.Root>
                            </Tooltip>
                        ))}
                        <Radio.Root
                            value={KICK_TEMPLATE_UPLOAD_SOURCE.source}
                            className={styles.sourceOption}
                        >
                            <DownloadIcon className="icon-color-gray-900 icon-size-md" />
                            <span className={styles.sourceOptionText}>
                                Kick template
                                <span className={styles.sourceOptionHint}>
                                    For any other system, or to start from
                                    scratch
                                </span>
                            </span>
                        </Radio.Root>
                    </div>
                </RadioGroup>
            </StandardModalBody>
            <StandardModalFooter>
                <ButtonWithLoader
                    variant="primary"
                    size="xl"
                    disabled={selectedSource === null || isPending}
                    loading={isPending}
                    onClick={() => {
                        if (selectedSource) {
                            onNext(selectedSource);
                        }
                    }}
                >
                    Next
                </ButtonWithLoader>
            </StandardModalFooter>
        </Modal>
    );
};
